import React, { type PropsWithChildren, useContext, useMemo } from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import ErrorBoundary from "~components/shared/errorBoundary";
import { GridModule } from "~components/shared/grid/gridModule";
import {
    InfoMode,
    moduleIsShadowWarning,
} from "~components/shared/info-mode/InfoMode";
import Header from "~components/shared/module-header/ModuleHeader";
import { PageContext } from "~context/PageContext";
import { VariantContext } from "~context/VariantContext";
import type { TrackingAttributes } from "~types/lineup";
import {
    type VariationComponent,
    type PreviewNotes,
    ModuleSource,
    ZoneColumnCombo,
} from "~types/types";
import { getCanonicalId, getProvenance } from "~utils/moduleProvenance";
import trackStoriesInModules from "~utils/trackStoriesInModules";

import styles from "./VariantBase.module.css";

export interface VariantBaseProps {
    className?: string;
    gridClassName?: string;
    source: VariationComponent;
    dataComponent?: string;
    supportedRegions?: ZoneColumnCombo[];
    withVariantHeader?: boolean;
    previewNotes?: PreviewNotes;
}

interface AbbaAttributes {
    "data-abba-config-key"?: string;
    "data-bound"?: "deferred";
}

export const VariantBase: React.FC<PropsWithChildren<VariantBaseProps>> = (
    props
) => {
    const {
        className,
        children,
        gridClassName,
        source,
        supportedRegions = [],
        withVariantHeader = false,
        previewNotes,
    } = props;

    const {
        id,
        abbaConfigKey,
        autofillData,
        isLastInColumn = false,
        renderingLocation = ZoneColumnCombo.unsupported,
        moduleIndex = 1,
        zoneName,
        tracking,
        items = [],
        componentName,
    } = source;
    const { useGrid, isModulePreview, isPreview } = useContext(PageContext);
    const variantContextValue = useMemo(
        () => ({ isInfoMode: isModulePreview, moduleId: id }),
        []
    );

    const moduleProvenance = getProvenance(source);
    const storyCount = items.length;

    const trackingAttributes: TrackingAttributes | null = trackStoriesInModules(
        {
            storyCount,
            moduleTracking: tracking,
            renderingLocation,
            isPhoenix: true,
            moduleTreatment: componentName,
            zoneName,
        }
    );

    const abbaAttributes: AbbaAttributes = {};

    if (abbaConfigKey) {
        abbaAttributes["data-abba-config-key"] = abbaConfigKey;
        abbaAttributes["data-bound"] = "deferred";
    }

    let validModule;

    // If a module declares its supportedRegions
    // and renderingLocation, those should be the
    // determinate of validity in either direction.
    // If a module does not pass these values, we
    // will only render if the useGrid = true. This will
    // prevent new modules from rendering on
    // old pages - BW 11/13/23 & 7/23/24
    if (supportedRegions.length > 0 && renderingLocation) {
        validModule = supportedRegions.includes(renderingLocation);
    } else {
        validModule = useGrid;
    }

    const unsupportedPlaceholder = (
        <div
            id={id}
            className={styles.unsupportedRegion}
            data-component="placeholder-module"
        />
    );

    const component = (
        <section
            className={createClasses(className, {
                "client-autofill": Boolean(autofillData),
            })}
            id={id}
            data-module-index={moduleIndex}
            data-canonical={getCanonicalId(source)}
            data-module-provenance={moduleProvenance}
            data-component={componentName}
            {...abbaAttributes}
            {...trackingAttributes}
        >
            {children}
        </section>
    );

    if (isModulePreview) {
        if (moduleIsShadowWarning(source)) {
            return (
                <ErrorBoundary>
                    <VariantContext.Provider value={variantContextValue}>
                        <GridModule
                            className={createClasses(
                                styles.shadowGrid,
                                "sdd-grid:bg-gray-200"
                            )}
                            isLastInColumn
                        >
                            <InfoMode
                                source={source}
                                withVariantHeader={withVariantHeader}
                                supportedRegions={supportedRegions}
                                previewNotes={previewNotes}
                            />
                        </GridModule>
                    </VariantContext.Provider>
                </ErrorBoundary>
            );
        }

        return (
            <ErrorBoundary>
                <VariantContext.Provider value={variantContextValue}>
                    <GridModule
                        className={createClasses(gridClassName, {
                            [styles.shadowGrid]:
                                moduleProvenance === ModuleSource.Shadow,
                            [styles.gridModuleWithHeader]: withVariantHeader,
                        })}
                        isLastInColumn={false}
                    >
                        {validModule ? component : unsupportedPlaceholder}
                    </GridModule>
                    <GridModule
                        className={createClasses("bg-gray-200", {
                            [styles.shadowGrid]:
                                moduleProvenance === ModuleSource.Shadow,
                        })}
                        isLastInColumn
                    >
                        <InfoMode
                            source={source}
                            withVariantHeader={withVariantHeader}
                            supportedRegions={supportedRegions}
                            previewNotes={previewNotes}
                        />
                    </GridModule>
                </VariantContext.Provider>
            </ErrorBoundary>
        );
    }

    if (isPreview) {
        return (
            <ErrorBoundary>
                <VariantContext.Provider value={variantContextValue}>
                    <GridModule
                        className={createClasses(gridClassName, {
                            [styles.shadowGrid]:
                                moduleProvenance === ModuleSource.Shadow,
                            [styles.gridModuleWithHeader]: withVariantHeader,
                        })}
                        isLastInColumn={isLastInColumn}
                    >
                        {validModule ? component : unsupportedPlaceholder}
                    </GridModule>
                </VariantContext.Provider>
            </ErrorBoundary>
        );
    }

    if (!validModule) return null;

    return (
        <ErrorBoundary>
            <VariantContext.Provider value={variantContextValue}>
                <GridModule
                    className={createClasses(gridClassName, {
                        [styles.shadowGrid]:
                            moduleProvenance === ModuleSource.Shadow,
                        [styles.gridModuleWithHeader]: withVariantHeader,
                    })}
                    isLastInColumn={isLastInColumn}
                >
                    {component}
                </GridModule>
            </VariantContext.Provider>
        </ErrorBoundary>
    );
};

export const VariantBaseWithHeader: React.FC<
    PropsWithChildren<VariantBaseProps>
> = (props) => {
    const { source, children } = props;

    return (
        <VariantBase withVariantHeader {...props}>
            <Header {...source} />
            <div className={styles.headerSiblingContainer}>{children}</div>
        </VariantBase>
    );
};
